import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import SearchableMedicineDropdown from "../../components/SearchableMedicineDropdown";
import {
    Plus,
    Trash2,
    Calendar,
    Pill,
    IndianRupee,
    ShoppingCart,
    AlertCircle,
    Save,
    Package,
    ArrowLeft,
    Hash,
    CalendarDays,
    Percent,
    Gift,
    Tag
} from "lucide-react";

const EditPurchase = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState('');
    const [medicines, setMedicines] = useState([]);
    const [supplier, setSupplier] = useState("");
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [medicineOptions, setMedicineOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [purchaseSummary, setPurchaseSummary] = useState({
        subtotal: 0, totalGst: 0, totalDiscount: 0, grandTotal: 0, totalItems: 0, totalUnits: 0
    });
    const [paymentStatus, setPaymentStatus] = useState('PENDING');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [errors, setErrors] = useState({});
    const [focusedRowId, setFocusedRowId] = useState(null); // New State for focus management

    const paymentStatusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'partial', label: 'Partial' },
        { value: 'completed', label: 'Completed' }
    ];
    const paymentMethodOptions = [
        { value: 'CASH', label: 'Cash' },
        { value: 'CHEQUE', label: 'Cheque' },
        { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
        { value: 'CREDIT', label: 'Credit' },
        { value: 'UPI', label: 'UPI' },
        { value: 'CARD', label: 'Card' }
    ];

    // === FETCH DATA ===
    useEffect(() => {
        const fetchAll = async () => {
            try {
                setIsLoading(true);
                const [supRes, medRes, purRes] = await Promise.all([
                    axiosInstance.get("/supplier").catch(() => ({ data: { suppliers: [] } })),
                    axiosInstance.get("/medicine").catch(() => ({ data: { medicines: [] } })),
                    axiosInstance.get(`/purchases/${id}`)
                ]);

                const suppliersData = Array.isArray(supRes.data) ? supRes.data : [];
                const medicinesData = Array.isArray(medRes.data.medicines) ? medRes.data.medicines : [];

                setSuppliers(suppliersData);
                setMedicineOptions(medicinesData);

                const purchase = purRes.data.purchase;
                setCurrentDate(purchase.purchase_date.split('T')[0]);
                setSupplier(purchase.supplier_id.toString());
                setInvoiceNumber(purchase.invoice_number || '');

                const statusMap = { 'PENDING': 'pending', 'PARTIAL': 'partial', 'PAID': 'completed' };
                setPaymentStatus(statusMap[purchase.payment_status] || 'pending');
                setPaymentMethod(purchase.payment_method || '');

                const supplierObj = suppliersData.find(s => s.id === purchase.supplier_id);
                setSelectedSupplier(supplierObj);

                const formatted = purchase.purchase_items.map((item, idx) => ({
                    id: item.id || Date.now() + idx,
                    medicine: item.medicine_id.toString(),
                    medicineName: item.medicine?.name || '',
                    batchNumber: item.batch_number,
                    expiryDate: item.expiry_date.split('T')[0],
                    manufactureDate: item.manufacture_date ? item.manufacture_date.split('T')[0] : '',
                    quantity: parseInt(item.quantity) || 0,
                    free_quantity: parseInt(item.free_quantity) || 0,
                    pricePerPackage: parseFloat(item.price_per_package) || 0,
                    mrp: parseFloat(item.mrp) || 0,
                    pricePerUnit: parseFloat(item.price_per_unit) || 0,
                    sellingPricePerPackage: parseFloat(item.selling_price_per_package) || 0,
                    sellingPricePerUnit: parseFloat(item.selling_price_per_unit) || 0,
                    totalPrice: parseFloat(item.total_price) || 0,
                    totalUnits: parseInt(item.total_units) || 0,
                    unitType: item.unit_type,
                    gstPercentage: parseFloat(item.gst_percentage) || 0,
                    discountPercentage: parseFloat(item.discount_percentage) || 0,
                    gstType: supplierObj?.is_interstate ? 'IGST' : 'CGST_SGST'
                }));

                setMedicines(formatted.length ? formatted : [{
                    id: Date.now(),
                    medicine: "", medicineName: "", batchNumber: "", expiryDate: "", manufactureDate: "",
                    quantity: 0, free_quantity: 0,
                    pricePerPackage: 0, mrp: 0, pricePerUnit: 0, sellingPricePerPackage: 0,
                    sellingPricePerUnit: 0, totalPrice: 0, totalUnits: 0, unitType: "",
                    gstPercentage: 0, discountPercentage: 0, gstType: 'CGST_SGST'
                }]);
            } catch (err) {
                console.error(err);
                showAlertMessage("Failed to load purchase", "error");
            } finally {
                setIsLoading(false);
            }
        };
        fetchAll();
    }, [id]);

    useEffect(() => {
        if (medicineOptions.length && medicines.length) {
            setMedicines(prev => prev.map(m => {
                const found = medicineOptions.find(opt => opt.id.toString() === m.medicine);
                return {
                    ...m,
                    medicineName: found ? found.name : m.medicineName
                };
            }));
        }
    }, [medicineOptions]);

    useEffect(() => {
        calculateTotals();
    }, [medicines]);

    const calculateTotals = () => {
        let subtotal = 0, totalGst = 0, totalDiscount = 0, totalItems = 0, totalUnits = 0;

        medicines.forEach(m => {
            if (m.medicine && m.quantity > 0) {
                const qty = parseFloat(m.quantity) || 0;
                const price = parseFloat(m.pricePerPackage) || 0;
                const gst = parseFloat(m.gstPercentage) || 0;
                const disc = parseFloat(m.discountPercentage) || 0;

                const itemSub = qty * price;
                const itemDisc = itemSub * (disc / 100);
                const afterDisc = itemSub - itemDisc;
                const itemGst = afterDisc * (gst / 100);

                subtotal += itemSub;
                totalDiscount += itemDisc;
                totalGst += itemGst;
                totalItems += qty;
                totalUnits += m.totalUnits || 0;
            }
        });

        const grand = subtotal - totalDiscount + totalGst;
        setPurchaseSummary({
            subtotal,
            totalGst,
            totalDiscount,
            grandTotal: grand,
            totalItems,
            totalUnits
        });
    };

    const handleMedicineChange = (index, field, value) => {
        const updated = [...medicines];
        updated[index][field] = value;

        if (["quantity", "free_quantity", "pricePerPackage", "mrp", "discountPercentage"].includes(field)) {
            const med = medicineOptions.find(m => m.id === parseInt(updated[index].medicine));
            if (med) {
                const qty = parseFloat(updated[index].quantity) || 0;
                const freeQty = parseFloat(updated[index].free_quantity) || 0;
                const price = parseFloat(updated[index].pricePerPackage) || 0;
                const mrp = parseFloat(updated[index].mrp) || 0;
                const unitsPerPkg = med.units_per_package || 1;

                updated[index].totalUnits = (qty + freeQty) * unitsPerPkg;
                updated[index].pricePerUnit = price / unitsPerPkg;
                updated[index].totalPrice = qty * price;

                if (mrp > 0) {
                    updated[index].sellingPricePerUnit = mrp / unitsPerPkg;
                    updated[index].sellingPricePerPackage = mrp;
                }
            }
        }
        setMedicines(updated);
    };

    const handleMedicineSelect = (index, selected) => {
        const updated = [...medicines];
        const batchDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        if (selected) {
            updated[index].medicine = selected.id;
            updated[index].medicineName = selected.name || '';
            updated[index].unitType = selected.uom;
            updated[index].gstPercentage = selected.gst_percentage || 0;
            updated[index].gstType = selectedSupplier?.is_interstate ? 'IGST' : 'CGST_SGST';
            updated[index].batchNumber = `BATCH${batchDate}${String(index + 1).padStart(2, '0')}`;
        } else {
            updated[index].medicine = "";
            updated[index].medicineName = "";
            updated[index].unitType = "";
            updated[index].batchNumber = "";
        }
        setMedicines(updated);

        // Robust Focus Shift to Batch Number
        if (selected) {
            setTimeout(() => {
                const batchInput = document.getElementById(`batch-${index}`);
                if (batchInput) {
                    batchInput.focus();
                    batchInput.select();
                }
            }, 100);
        }
    };

    const addMedicine = () => {
        const newId = Date.now();
        setMedicines(prev => [...prev, {
            id: newId,
            medicine: "", medicineName: "", batchNumber: "", expiryDate: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split("T")[0], manufactureDate: new Date().toISOString().split("T")[0],
            quantity: 0, free_quantity: 0,
            pricePerPackage: 0, mrp: 0, pricePerUnit: 0, sellingPricePerPackage: 0,
            sellingPricePerUnit: 0, totalPrice: 0, totalUnits: 0, unitType: "",
            gstPercentage: 0, discountPercentage: 0,
            gstType: selectedSupplier?.is_interstate ? 'IGST' : 'CGST_SGST'
        }]);
        setFocusedRowId(newId);
    };

    const removeMedicine = (id) => {
        if (medicines.length > 1) {
            setMedicines(medicines.filter(m => m.id !== id));
        }
    };

    const showAlertMessage = (msg, type) => {
        setAlertMessage(msg);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const validateForm = () => {
        if (!supplier) return showAlertMessage("Select supplier", "error");
        for (let i = 0; i < medicines.length; i++) {
            const m = medicines[i];
            if (!m.medicine) return showAlertMessage(`Select medicine #${i + 1}`, "error");
            if (!m.batchNumber) return showAlertMessage(`Enter batch #${i + 1}`, "error");
            if (!m.quantity || m.quantity <= 0) return showAlertMessage(`Valid qty #${i + 1}`, "error");
            if (!m.pricePerPackage || m.pricePerPackage <= 0) return showAlertMessage(`Valid price #${i + 1}`, "error");
            if (new Date(m.expiryDate) <= new Date()) return showAlertMessage(`Future expiry #${i + 1}`, "error");
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const payload = {
                supplier_id: parseInt(supplier),
                purchase_date: currentDate,
                payment_status: paymentStatus,
                payment_method: paymentMethod,
                invoice_number: invoiceNumber,
                discount_amount: parseFloat(purchaseSummary.totalDiscount),
                total_amount: parseFloat(purchaseSummary.grandTotal),
                total_gst_amount: parseFloat(purchaseSummary.totalGst),
                items: medicines.map(m => ({
                    purchase_item_id: m.id,
                    medicine_id: parseInt(m.medicine),
                    batch_number: m.batchNumber,
                    expiry_date: m.expiryDate,
                    manufacture_date: m.manufactureDate,
                    quantity: parseInt(m.quantity),
                    free_quantity: parseInt(m.free_quantity) || 0,
                    total_units: parseInt(m.totalUnits),
                    price_per_package: parseFloat(m.pricePerPackage),
                    mrp: parseFloat(m.mrp) || 0,
                    price_per_unit: parseFloat(m.pricePerUnit),
                    selling_price_per_package: parseFloat(m.sellingPricePerPackage),
                    selling_price_per_unit: parseFloat(m.sellingPricePerUnit),
                    total_price: parseFloat(m.totalPrice),
                    gst_percentage: parseFloat(m.gstPercentage),
                    gst_type: m.gstType,
                    discount_percentage: parseFloat(m.discountPercentage || 0)
                }))
            };

            await axiosInstance.put(`/purchases/${id}`, payload);
            navigate('/purchase', { state: { successMessage: 'Purchase Updated!' } });
        } catch (err) {
            const msg = err.response?.data?.error || "Update failed";
            showAlertMessage(msg, "error");
            setErrors(err.response?.data?.errors || { general: msg });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <Authenticated>
                <div className="p-4 bg-gradient-to-b from-cyan-50 to-white border-2 border-cyan-600 m-2">
                    <div className="animate-pulse">
                        <div className="h-10 bg-cyan-200 rounded mb-2"></div>
                        <div className="grid grid-cols-12 gap-1">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="h-8 bg-cyan-100 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </Authenticated>
        );
    }
    const handleEnterKey = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const focusable = document.querySelectorAll('input, select, textarea, button, [tabindex="0"]');
            const list = Array.from(focusable).filter(el => !el.disabled && el.tabIndex !== -1);
            const index = list.indexOf(e.target);
            if (index > -1 && index < list.length - 1) {
                list[index + 1].focus();
            }
        }
    };

    const handleLastFieldEnter = (e, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (index === medicines.length - 1) {
                addMedicine();
            } else {
                // Focus next row medicine
                setFocusedRowId(medicines[index + 1].id);
            }
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 bg-white hover:bg-gray-100 rounded-lg border border-gray-200 transition-all"
                            >
                                <ArrowLeft className="w-5 h-5 text-gray-700" />
                            </button>
                            <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-3 rounded-xl shadow-md">
                                <Plus className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Purchase</h1>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">Update purchase entry</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                            <Calendar className="w-4 h-4 text-teal-600" />
                            <span className="font-medium">Date:</span>
                            <input
                                type="date"
                                value={currentDate}
                                onChange={e => setCurrentDate(e.target.value)}
                                onKeyDown={handleEnterKey}
                                className="px-2 py-1 border-0 focus:ring-0 text-gray-700 font-medium bg-transparent cursor-pointer"
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                        {/* Supplier & Invoice Section */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 pb-8 border-b border-gray-200">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier <span className="text-red-500">*</span></label>
                                <select
                                    value={supplier}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSupplier(id);
                                        const sup = suppliers.find(s => s.id === parseInt(id));
                                        setSelectedSupplier(sup);
                                        setMedicines(medicines.map(m => ({ ...m, gstType: sup?.is_interstate ? 'IGST' : 'CGST_SGST' })));
                                    }}
                                    onKeyDown={handleEnterKey}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                >
                                    <option value="">Select Supplier</option>
                                    {Array.isArray(suppliers) && suppliers.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice # <span className="text-red-500">*</span></label>
                                <input
                                    value={invoiceNumber}
                                    onChange={e => setInvoiceNumber(e.target.value)}
                                    onKeyDown={handleEnterKey}
                                    placeholder="Enter invoice number"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Status <span className="text-red-500">*</span></label>
                                <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)} onKeyDown={handleEnterKey} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all">
                                    {paymentStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method <span className="text-red-500">*</span></label>
                                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} onKeyDown={handleEnterKey} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all">
                                    <option value="">Select Method</option>
                                    {paymentMethodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Medicines Section Header */}
                        <div className="mb-6">
                            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
                                <Package className="w-5 h-5 text-teal-600" />
                                Items
                            </h2>
                        </div>

                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 mb-6">
                            <button onClick={addMedicine} className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow-md font-medium">
                                <Plus size={16} /> Add Item
                            </button>
                        </div>

                        <div className="space-y-4 mb-6">
                            {medicines.map((m, i) => (
                                <div key={m.id} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-teal-100 text-teal-700 font-bold w-10 h-10 rounded-full flex items-center justify-center text-lg">
                                                {i + 1}
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-800">
                                                {m.medicineName || "Select Medicine"}
                                            </h3>
                                        </div>
                                        {medicines.length > 1 && (
                                            <button
                                                onClick={() => removeMedicine(m.id)}
                                                className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all hover:scale-105"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Medicine Selection Row */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <Pill className="w-4 h-4 text-teal-600" />
                                                Medicine Name <span className="text-red-500">*</span>
                                            </label>
                                            <SearchableMedicineDropdown
                                                value={m.medicine}
                                                onChange={med => handleMedicineSelect(i, med)}
                                                onKeyDown={handleEnterKey}
                                                placeholder="Search and select medicine..."
                                                defaultDisplay={m.medicineName}
                                                autoFocus={m.id === focusedRowId}
                                                id={`medicine-dropdown-${i}`}
                                            />
                                        </div>
                                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <Hash className="w-4 h-4 text-teal-600" />
                                                Batch Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id={`batch-${i}`}
                                                value={m.batchNumber}
                                                onChange={e => handleMedicineChange(i, "batchNumber", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                placeholder="Enter batch number"
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Dates Row */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                            <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                                <CalendarDays className="w-4 h-4 text-blue-600" />
                                                Manufacture Date
                                            </label>
                                            <input
                                                type="date"
                                                value={m.manufactureDate}
                                                onChange={e => handleMedicineChange(i, "manufactureDate", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                            />
                                        </div>
                                        <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                                            <label className="block text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                                                <CalendarDays className="w-4 h-4 text-amber-600" />
                                                Expiry Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={m.expiryDate}
                                                onChange={e => handleMedicineChange(i, "expiryDate", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Pricing & Quantity Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-purple-50 p-3 rounded-xl border border-purple-200">
                                            <label className="block text-xs font-semibold text-purple-900 mb-2 flex items-center gap-1">
                                                <ShoppingCart className="w-3 h-3" />
                                                Qty
                                            </label>
                                            <input
                                                type="number"
                                                value={m.quantity}
                                                onChange={e => handleMedicineChange(i, "quantity", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-cyan-50 p-3 rounded-xl border border-cyan-200">
                                            <label className="block text-xs font-semibold text-cyan-900 mb-2 flex items-center gap-1">
                                                <Gift className="w-3 h-3" />
                                                Free Qty
                                            </label>
                                            <input
                                                type="number"
                                                value={m.free_quantity}
                                                onChange={e => handleMedicineChange(i, "free_quantity", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-cyan-300 rounded-lg focus:ring-2 focus:ring-cyan-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-green-50 p-3 rounded-xl border border-green-200">
                                            <label className="block text-xs font-semibold text-green-900 mb-2 flex items-center gap-1">
                                                <IndianRupee className="w-3 h-3" />
                                                Price/Pkg
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.pricePerPackage}
                                                onChange={e => handleMedicineChange(i, "pricePerPackage", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 text-right font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-yellow-50 p-3 rounded-xl border border-yellow-200">
                                            <label className="block text-xs font-semibold text-yellow-900 mb-2 flex items-center gap-1">
                                                <Tag className="w-3 h-3" />
                                                MRP
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.mrp}
                                                onChange={e => handleMedicineChange(i, "mrp", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-right font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-orange-50 p-3 rounded-xl border border-orange-200">
                                            <label className="block text-xs font-semibold text-orange-900 mb-2 flex items-center gap-1">
                                                <Percent className="w-3 h-3" />
                                                GST %
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.gstPercentage}
                                                onChange={e => handleMedicineChange(i, "gstPercentage", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-pink-50 p-3 rounded-xl border border-pink-200">
                                            <label className="block text-xs font-semibold text-pink-900 mb-2 flex items-center gap-1">
                                                <Percent className="w-3 h-3" />
                                                Disc %
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.discountPercentage}
                                                onChange={e => handleMedicineChange(i, "discountPercentage", e.target.value)}
                                                onKeyDown={(e) => handleLastFieldEnter(e, i)}
                                                className="w-full px-3 py-2 border border-pink-300 rounded-lg focus:ring-2 focus:ring-pink-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-indigo-50 p-3 rounded-xl border border-indigo-200">
                                            <label className="block text-xs font-semibold text-indigo-900 mb-2">
                                                Units
                                            </label>
                                            <div className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-center font-bold text-indigo-700 bg-white">
                                                {m.totalUnits}
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-3 rounded-xl border-2 border-teal-300">
                                            <label className="block text-xs font-semibold text-teal-900 mb-2 flex items-center gap-1">
                                                <IndianRupee className="w-3 h-3" />
                                                Total
                                            </label>
                                            <div className="w-full px-3 py-2 border-2 border-teal-400 rounded-lg text-center font-bold text-teal-700 bg-white text-lg">
                                                ₹{parseFloat(m.totalPrice || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Summary Section */}
                        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl shadow-lg p-6 text-white mb-6">
                            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Subtotal</p>
                                    <p className="text-lg font-bold">₹{purchaseSummary.subtotal.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Discount</p>
                                    <p className="text-lg font-bold">₹{purchaseSummary.totalDiscount.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">GST</p>
                                    <p className="text-lg font-bold">₹{purchaseSummary.totalGst.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-20 rounded-lg border border-white border-opacity-30">
                                    <p className="text-xs font-semibold opacity-90 mb-1">Grand Total</p>
                                    <p className="text-xl font-bold">₹{purchaseSummary.grandTotal.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Items</p>
                                    <p className="text-lg font-bold">{purchaseSummary.totalItems}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Units</p>
                                    <p className="text-lg font-bold">{purchaseSummary.totalUnits}</p>
                                </div>
                            </div>
                        </div>

                        {/* Alert */}
                        {showAlert && (
                            <div className={`mb-6 p-4 round e d-x l  border $ { alertType == = "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"} flex items -center gap- 2 `}>
                                <AlertCircle size={20} />
                                <span className="font-medium">{alertMessage}</span>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => navigate(-1)} className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-all font-medium shadow-sm hover:shadow-md">
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-xl hover:shadow-lg disabled:opacity-50 transition-all font-medium shadow-md"
                            >
                                <Save size={18} />
                                {isSubmitting ? "Updating..." : "Update Purchase"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default EditPurchase;